import { useContext, useState } from 'react';
import {
	currentIsoDate,
	formatDate,
	getDayJsFromIso,
	getMonthsList,
	getWeekDays,
	avoidSaturdayDate,
	getDayHourList,
	dayJS,
} from '../../../utils/date';
import Select from 'react-select';
import {
	ClientContext,
	ExpertContext,
	ProfileContext,
} from '../../Contexts/contexts';
import { SelectHourData } from '../../../data/SelectHourData';
import dayjs from 'dayjs';
import CalendarAppointment from './CalendarAppointment';

function Calendar() {
	const profileContext = useContext(ProfileContext);
	const expertContext = useContext(ExpertContext);
	const clientContext = useContext(ClientContext);

	const [monthList] = useState(getMonthsList());
	const [currentMonth, setCurrentMonth] = useState({
		value: formatDate(currentIsoDate, 'MMMM'),
		label: formatDate(currentIsoDate, 'MMMM'),
		number: parseInt(dayjs(currentIsoDate).month()),
	});
	const [weeksDay, setWeeksDay] = useState(getWeekDays());
	const [currentDate, setCurrentDate] = useState(
		avoidSaturdayDate(currentIsoDate)
	);

	const onChange = (selectedMonth) => {
		const newDate = getDayJsFromIso(currentDate).month(selectedMonth?.number);

		setCurrentMonth(selectedMonth);
		setWeeksDay(getWeekDays(newDate.toISOString()));
		setCurrentDate(avoidSaturdayDate(newDate.toISOString()));
	};

	function dateDayClasses(dayDate) {
		let classes = 'calendar-week-date';

		if (dayDate === formatDate(currentDate, 'DD'))
			classes += ' calendar-week-date-current';

		return classes;
	}

	function onSelectWeekDay(isoDate) {
		if (getDayJsFromIso(isoDate).month() !== currentMonth.number)
			setCurrentMonth(monthList[getDayJsFromIso(isoDate).month()]);

		setWeeksDay(getWeekDays(isoDate));
		setCurrentDate(avoidSaturdayDate(isoDate));
	}

	function UpdateCurrentWeek(next = true) {
		const dayObj = dayJS(currentDate);
		const newWeek = next ? dayObj.add(1, 'week') : dayObj.subtract(1, 'week');
		const weekIsoDate = newWeek.toISOString();

		if (getDayJsFromIso(weekIsoDate).month() !== currentMonth.number)
			setCurrentMonth(monthList[getDayJsFromIso(weekIsoDate).month()]);

		setWeeksDay(getWeekDays(weekIsoDate));
		setCurrentDate(avoidSaturdayDate(weekIsoDate));
	}

	function placeAppointmentsOnGrid() {
		let appointments = [];

		// get the current context
		let usedContext = undefined;

		if (profileContext.isExpert) usedContext = expertContext;
		else if (profileContext.isClient) usedContext = clientContext;

		if (usedContext === undefined) return;

		// get the current day appointments

		const currentAppointments = usedContext?.appointments?.filter(
			(appointment) => {
				if (!appointment.acceptedByClient || !appointment.acceptedByExpert)
					return false;

				const appointmentDate = getDayJsFromIso(appointment.appointmentDate);

				return (
					appointmentDate.month() === currentMonth.number &&
					appointmentDate.year() === getDayJsFromIso(currentDate).year()
				);
			}
		);

		const maxRows = SelectHourData.length; // available hours
		let rowCount = 1; // grid start line is at index 1

		for (let i = 0; i < maxRows; i++) {
			// check if 30min * i is the appointment date
			const calculatedDate = getDayJsFromIso(currentDate)
				.hour(9)
				.minute(0)
				.add(i * 30, 'minutes');

			const sameDate = currentAppointments?.find((appointment) => {
				return calculatedDate.isSame(
					getDayJsFromIso(appointment.appointmentDate),
					'minutes'
				);
			});

			if (sameDate !== undefined && sameDate !== null) {
				const clientExpertRelation = usedContext.getRelationByIds(
					sameDate.clientExpertRelationshipId
				);

				if (!clientExpertRelation) continue;

				appointments.push(
					<div
						key={clientExpertRelation?.id}
						style={{
							gridRow: `${rowCount} / ${rowCount + 1}`,
						}}
						className="calendar-planning-appointment"
					>
						<CalendarAppointment
							clientExpertRelation={clientExpertRelation}
							isExpert={profileContext?.isExpert}
							hour={dayJS(sameDate.appointmentDate).format('HH:mm')}
						/>
					</div>
				);
			} else {
				appointments.push(
					<span
						key={`empty-${i}`}
						className="calendar-planning-empty"
						style={{
							gridRow: `${rowCount} / ${rowCount + 1}`,
						}}
					></span>
				);
			}

			rowCount += 1;
		}

		return appointments;
	}

	return (
		<>
			<div className="calendar-container">
				<div className="calendar-header-row">
					<div className="title">Calendrier</div>
					<Select
						options={monthList}
						onChange={onChange}
						value={currentMonth}
						className="calendar-select"
						classNamePrefix="calendar-select"
						isSearchable={false}
					/>
				</div>
				<div className="calendar-week-line">
					<button
						type="button"
						className="calendar-week-previous calendar-week-nav-btn"
						onClick={() => UpdateCurrentWeek(false)}
					></button>
					<div className="calendar-week-row">
						{weeksDay.map(({ day, isoDate }) => (
							<span
								className="calendar-week-day"
								key={day}
								onClick={() => onSelectWeekDay(isoDate)}
							>
								{day.slice(0, -1)}
							</span>
						))}
						{weeksDay.map(({ dayDate, isoDate }) => (
							<span
								className={dateDayClasses(dayDate)}
								key={dayDate}
								onClick={() => onSelectWeekDay(isoDate)}
							>
								{dayDate}
							</span>
						))}
					</div>
					<button
						type="button"
						className="calendar-week-next calendar-week-nav-btn"
						onClick={() => UpdateCurrentWeek()}
					></button>
				</div>

				<div className="calendar-planning-row">
					<div className="calendar-planning-scroller">
						<div className="calendar-planning-hours">
							{getDayHourList(currentDate).map((hour) => (
								<div key={hour} className="calendar-planning-hour">
									{hour}
								</div>
							))}
						</div>
						<div className="calendar-planning-appointments">
							{placeAppointmentsOnGrid()}
						</div>
					</div>
				</div>
			</div>
		</>
	);
}

export default Calendar;
